import {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
} from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  FormLabel,
  Divider,
  Alert,
  FormHelperText,
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useGetUsers } from "../../../shared/hooks";
import { AutoComplete, DataLayout, FieldControl } from "../../../shared/components";
import { useFetchApproval } from "../../stageGates/hooks/useFetchApprovals";
import { useFetchChecklist } from "../../stageGates/hooks/useFetchChecklist";
import { ProjectArtefact } from "../../artefacts/component/ProjectArtefact";
import { useStageGate } from "../../projectOverview/hooks/useStageGate";
import { useCapabilities } from "../../../hooks/useCapabilities";

export interface StageGateFormHandle {
  submit: () => void;
}

interface StageGateFormProps {
  onSave: (data: StageGateFormData) => void;
  onCancel: () => void;
  initialData?: Partial<StageGateFormData>;
  stageGateId: number | null;
  isEditing?: boolean;
  phaseOptions?: Array<{ id: number; name: string }>;
  projectId: string | undefined;
}

export interface StageGateFormData {
  title: string;
  description: string;
  owner: string;
  userId?: number;
  plannedCompletionDate: Dayjs;
  actualCompletionDate?: Dayjs;
  parentPhaseId?: number;
  parentPhase?: string;
}

export const StageGateForm = forwardRef<
  StageGateFormHandle,
  StageGateFormProps
>(function StageGateForm({ onSave, onCancel, initialData, stageGateId, isEditing, phaseOptions = [], projectId }, ref) {

  const { can } = useCapabilities();

  const isAllowedAddApprover = can("operational_delivery:project:approval:add");
  const isAllowedAddChecklist = can("operational_delivery:project:checklist:add");

  const isAllowedEditApprover = can("operational_delivery:project:approval:edit");
  const isAllowedEditChecklist = can("operational_delivery:project:checklist:edit");

  const [currentTab, setCurrentTab] = useState(0);

  /** ================= USERS ================= */
  const { data: usersData, isLoading: isLoadingUsers } = useGetUsers();
  const userOptions = useMemo(() => {
    if (!usersData?.data) return [];
    return usersData.data
      .filter((u) => u.is_active === "1")
      .map((u) => ({
        id: u.user_id,
        name: `${u.first_name} ${u.last_name}`,
      }));
  }, [usersData]);

  /** ================= HOOKS ================= */

  const { removeCache } = useStageGate(projectId);

  const [formStatusApprover, setFormStatusApprover] = useState(false);
  const {
    data: approvals,
    handleAssignApprover,
    handleUpdateApprover,
    isPending: isFetchingApprover,
    isAdding: isAddingApprover,
    isEditing: isEditingApprover,
    isUpdating: isUpdatingApprover,
  } = useFetchApproval({ stageID: String(stageGateId), projectId: projectId! });

  const [formStatusChecklist, setFormStatusChecklist] = useState(false);
  const {
    data: checklists,
    handleAddChecklist,
    handleEditChecklist,
    isPending: isFetchingCheckList,
    isAdding: isAddingChecklist,
    isEditing: isEditingChecklist,
  } = useFetchChecklist(String(stageGateId));

  const [isGateDecisionExist, setIsGateDecisionExist] = useState(false);

  useEffect(() => {
    let status =
      approvals &&
      approvals.find(
        (value: { is_decision_gate: any }) => value.is_decision_gate
      );
    setIsGateDecisionExist(!!status);
  }, [approvals]);

  /** ================= FORM DATA ================= */
  const [formData, setFormData] = useState<StageGateFormData>({
    title: initialData?.title || "",
    description: initialData?.description || "",
    owner: initialData?.owner || "",
    userId: initialData?.userId,
    plannedCompletionDate: initialData?.plannedCompletionDate || dayjs(),
    actualCompletionDate: initialData?.actualCompletionDate,
    parentPhaseId: initialData?.parentPhaseId,
    parentPhase: initialData?.parentPhase || "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        title: initialData.title ?? prev.title,
        description: initialData.description ?? prev.description,
        owner: initialData.owner ?? prev.owner,
        userId: initialData.userId ?? prev.userId,
        plannedCompletionDate:
          initialData.plannedCompletionDate ?? prev.plannedCompletionDate,
        actualCompletionDate:
          initialData.actualCompletionDate ?? prev.actualCompletionDate,
        parentPhaseId: initialData.parentPhaseId ?? prev.parentPhaseId,
        parentPhase: initialData.parentPhase ?? prev.parentPhase,
      }));
    }
  }, [initialData]);

  /** ================= APPROVAL STATE ================= */
  const [approver, setApprover] = useState<{
    id?: number;
    isEditing?: boolean;
    status: boolean;
    userId: number | null;
    isGateDecision: boolean;
  }>({
    status: false,
    userId: null,
    isGateDecision: false,
    isEditing: false,
  });

  /** ================= CHECKLIST STATE ================= */
  const [checkList, setCheckList] = useState<{
    id?: number;
    isEditing?: boolean;
    task_name: string | null;
    userId: number | null;
    owner: string | null;
    isEvidenceReq: boolean;
  }>({
    task_name: null,
    userId: null,
    owner: null,
    isEvidenceReq: false,
    isEditing: false,
  });

  /** ================= HANDLERS ================= */
  const handleApproverSubmit = async () => {
    if (!approver.userId) return;

    const payload = {
      user_id: approver.userId,
      is_decision_gate: approver.isGateDecision,
      work_stream_id: 5,
    };

    if (approver.isEditing && approver.id) {
      await handleUpdateApprover(String(approver.id), payload);
    } else {
      await handleAssignApprover(payload);
    }
    removeCache();
    setApprover({
      status: false,
      userId: null,
      isGateDecision: false,
      isEditing: false,
    });
  };

  const handleChecklistSubmit = async () => {
    if (!checkList.task_name || !checkList.userId) return;
    const payload = {
      task_name: checkList.task_name,
      user_id: checkList.userId,
      is_evidence_required: checkList.isEvidenceReq,
      is_required: false,
      object_id: stageGateId || null,
      object_type: "checklist",
      project_id: Number(projectId),
    };
    if (checkList.isEditing && checkList.id) {
      await handleEditChecklist(String(checkList.id), payload);
    } else {
      await handleAddChecklist(payload);
    }
    removeCache();
    setCheckList({
      task_name: null,
      userId: null,
      owner: null,
      isEvidenceReq: false,
      isEditing: false,
    });
  };

  const handleEditApprover = (approval: any) => {
    setApprover({
      status: true,
      isEditing: true,
      userId: approval.user_id,
      isGateDecision: approval.is_decision_gate,
      id: approval.id,
    });
  };

  const handleEditCheckList = (item: any) => {
    setCheckList({
      task_name: item.task_name,
      userId: item.user_id,
      owner: item.owner,
      isEvidenceReq: item.is_evidence_required,
      id: item.task_id,
      isEditing: true,
    });
  };

  const [errors, setErrors] = useState<{ parentPhaseId?: string; title?: string }>({});

  useImperativeHandle(ref, () => ({
    submit: () => {
      const newErrors: { parentPhaseId?: string; title?: string } = {};
      if (!formData.parentPhaseId) {
        newErrors.parentPhaseId = "Parent Phase is required";
      }
      if (!formData.title) {
        newErrors.title = "Stage Gate is required";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      onSave(formData);
    },
  }));

  /** ================= UI RENDER ================= */
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

      <Tabs
        value={currentTab}
        onChange={(_, val) => setCurrentTab(val)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
      >
        <Tab label="OVERVIEW" />
        <Tab label="APPROVALS" disabled={!stageGateId} />
        <Tab label="CHECKLIST" disabled={!stageGateId} />
        <Tab label="ARTEFACT" disabled={!stageGateId} />
      </Tabs>

      {currentTab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: 2 }}>

          {isEditing ? (
            <TextField
              label="Stage Gate"
              value={formData.title}
              fullWidth
              disabled
            />
          ) : (
            <AutoComplete
              value={formData.title}
              onSelect={(value) => {
                setFormData({ ...formData, title: value })
                setErrors({ ...errors, title: undefined });
              }}
              name={"stage-gates"}
              isLabelRequired
              error={!!errors.title}
              helperText={errors.title}
            />
          )}

          <TextField
            label="Stage Gate Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />

          <FormControl fullWidth error={!!errors.parentPhaseId}>
            <InputLabel>Parent Phase</InputLabel>
            <Select
              value={formData.parentPhaseId || ""}
              label="Parent Phase"
              onChange={(e) => {
                const selectedPhase = phaseOptions.find(
                  (p) => p.id === e.target.value
                );
                setFormData({
                  ...formData,
                  parentPhaseId: e.target.value as number,
                  parentPhase: selectedPhase?.name || "",
                });
                setErrors({ ...errors, parentPhaseId: undefined });
              }}
              disabled={isEditing}
            >
              {phaseOptions.length > 0 ? (
                phaseOptions.map((phase) => (
                  <MenuItem key={phase.id} value={phase.id}>
                    {phase.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No phases available
                </MenuItem>
              )}
            </Select>
            {errors.parentPhaseId && (
              <FormHelperText>{errors.parentPhaseId}</FormHelperText>
            )}
          </FormControl>

          <FieldControl
            keyName={"userId"}
            label={"Approver"}
            variant={"select"}
            value={formData.userId}
            options={userOptions.map((u) => ({
              value: u.id,
              label: u.name,
            }))}
            onChange={(_key: string, value: any) => {
              setFormData({
                ...formData,
                userId: value,
              })
            }}
          />

          <DateTimePicker
            label="Planned Completion Date"
            value={formData.plannedCompletionDate}
            onChange={(date) =>
              date &&
              setFormData({ ...formData, plannedCompletionDate: date })
            }
            slotProps={{ textField: { fullWidth: true } }}
          />

          {/* <DateTimePicker
              label="Actual Completion Date"
              value={formData.actualCompletionDate || null}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  actualCompletionDate: date || undefined,
                })
              }
              slotProps={{ textField: { fullWidth: true } }}
            /> */}
        </Box>
      )}

      {/* APPROVALS TAB */}
      {currentTab === 1 && (
        <Box px={2} display={'flex'} flexDirection={'column'} gap={2}>
          {
            !formStatusApprover && isAllowedAddApprover && (
              <Button onClick={() => setFormStatusApprover(true)} variant="outlined">Create Approver</Button>
            )
          }
          {
            formStatusApprover && (
              <DataLayout title="Approvals" bgVariant="filled">
                <Box p={2}>
                  <FieldControl
                    keyName={"userId"}
                    label={"Stage Owner"}
                    variant={"select"}
                    value={approver.userId}
                    options={userOptions.map((u) => ({
                      value: u.id,
                      label: u.name,
                    }))}
                    onChange={(_key: string, value: any) => {
                      setApprover({
                        ...approver,
                        userId: value,
                      })
                    }}
                  />

                  <Box display="flex" alignItems="center" mt={1}>
                    <Checkbox
                      disabled={!approver.userId}
                      checked={approver.isGateDecision}
                      onChange={() =>
                        setApprover({
                          ...approver,
                          isGateDecision: !approver.isGateDecision,
                        })
                      }
                    />
                    <FormLabel>Select if this person is gate decision maker</FormLabel>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      disabled={
                        isAddingApprover || isEditingApprover || isUpdatingApprover || (!approver.userId)
                      }
                      onClick={() => {
                        setFormStatusApprover(false)
                        setIsGateDecisionExist(false)
                        setApprover({
                          status: false,
                          userId: null,
                          isGateDecision: false,
                          isEditing: false,
                        })
                      }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleApproverSubmit}
                      disabled={
                        isAddingApprover || isEditingApprover || isUpdatingApprover || (!approver.userId)
                      }
                      startIcon={
                        isAddingApprover ||
                          isEditingApprover ||
                          isUpdatingApprover ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : approver.isEditing ? (
                          <Edit />
                        ) : (
                          <Add />
                        )
                      }
                    >
                      {approver.isEditing ? "Update Approver" : "Save Approver"}
                    </Button>
                  </Box>
                </Box>
              </DataLayout>
            )
          }
          <Box>
            {isFetchingApprover ? (
              <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                gap={2}
                py={2}
              >
                <CircularProgress />
              </Box>
            ) : approvals?.length === 0 ? (
              <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
              >
                <Alert severity="warning">No approvers assigned</Alert>
              </Box>
            ) : (
              approvals?.map((approval: any) => (
                <Box
                  key={approval.id}
                  border="1px solid"
                  borderColor="divider"
                  borderRadius={1}
                  p={2}
                  mb={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="body2">
                      {approval.user_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {approval.role_name}
                    </Typography>
                  </Box>
                  {
                    isAllowedEditApprover && (
                      <Button
                        startIcon={<Edit />}
                        onClick={() => handleEditApprover(approval)}
                      />
                    )
                  }
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}

      {/* CHECKLIST TAB */}
      {currentTab === 2 && (
        <Box px={2} display={'flex'} flexDirection={'column'} gap={2}>
          {
            !formStatusChecklist && isAllowedAddChecklist && (
              <Button onClick={() => setFormStatusChecklist(true)} variant="outlined">Create Checklist</Button>
            )
          }
          {
            formStatusChecklist && (
              <DataLayout title="Checklist" bgVariant="filled">
                <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>
                  <FieldControl
                    variant="input"
                    label="Checklist Title"
                    keyName="task_name"
                    value={checkList.task_name}
                    onChange={(_key: string, value: any) => {
                      setCheckList({ ...checkList, task_name: value })
                    }}
                  />
                  <FieldControl
                    keyName={"userId"}
                    label={"Approver"}
                    variant={"select"}
                    value={checkList.userId}
                    options={userOptions.map((u) => ({
                      value: u.id,
                      label: u.name,
                    }))}
                    onChange={(_key: string, value: any) => {
                      setCheckList({
                        ...checkList,
                        userId: value,
                      })
                    }}
                  />
                  <Divider />
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="text"
                      onClick={() => {
                        setFormStatusChecklist(false)
                        setCheckList({
                          task_name: null,
                          userId: null,
                          owner: null,
                          isEvidenceReq: false,
                          isEditing: false,
                        })
                      }}
                      disabled={isAddingChecklist || isEditingChecklist}
                    >
                      Cancel
                    </Button>
                    <Button
                      startIcon={
                        isAddingChecklist || isEditingChecklist ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : checkList.isEditing ? (
                          <Edit />
                        ) : (
                          <Add />
                        )
                      }
                      variant="contained"
                      onClick={handleChecklistSubmit}
                      disabled={isAddingChecklist || isEditingChecklist}
                    >
                      {checkList.isEditing ? "Update Item" : "Add Item"}
                    </Button>
                  </Box>
                </Box>
              </DataLayout>
            )
          }
          <Box>
            {isFetchingCheckList ? (
              <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                gap={2}
                py={2}
              >
                <CircularProgress />
              </Box>
            ) : checklists?.length === 0 ? (
              <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
              >
                <Alert severity="warning">No checklist assigned</Alert>
              </Box>
            ) : (
              checklists?.map((item: any) => (
                <Box
                  key={item.id}
                  border="1px solid"
                  borderColor="divider"
                  borderRadius={1}
                  p={2}
                  mb={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="body2">{item.task_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Owner : {item.owner}
                    </Typography>
                  </Box>
                  {
                    isAllowedEditChecklist && (
                      <Button
                        startIcon={<Edit />}
                        onClick={() => handleEditCheckList(item)}
                      />
                    )
                  }
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}

      {
        currentTab === 3 && (
          <Box height={'100%'} overflow={'hidden'} display={'flex'}>
            <ProjectArtefact projectId={projectId} />
          </Box>
        )
      }

    </Box>
  );
});

export const StageGateFormActions: React.FC<{
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
  isDeleteAllowed?: boolean;
}> = ({ onSave, onCancel, onDelete, isEditing, isDeleteAllowed }) => (
  <>
    <Button
      variant="outlined"
      onClick={onCancel}
      sx={{ textTransform: "uppercase" }}
    >
      Cancel
    </Button>
    {isEditing && onDelete && isDeleteAllowed && (
      <Button
        variant="outlined"
        color="error"
        onClick={onDelete}
        sx={{ textTransform: "uppercase" }}
      >
        Delete
      </Button>
    )}
    <Button
      variant="contained"
      onClick={onSave}
      sx={{ textTransform: "uppercase", bgcolor: "#E84B0F" }}
    >
      Save Details
    </Button>
  </>
);
